import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, User, FileText, Wallet, FolderOpen, History } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";
import { AdminPageHero } from "../../modules/admin/ui";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../config/adminRoutes";

type TabId = "dossier" | "contrat" | "paie" | "documents" | "historique";

export default function EmployeRHDetailPage(): React.JSX.Element {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const canManage = hasPermission(user, "rhEmployes.manage");
  const showLierForm = params.get("lier") === "1" && canManage;

  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabId>("dossier");
  const [lierAgentId, setLierAgentId] = useState("");
  const [lierLoading, setLierLoading] = useState(false);

  const load = () => {
    const raw = Number(id);
    if (!Number.isInteger(raw) || raw <= 0) {
      toast.error("Identifiant invalide.");
      setLoading(false);
      return;
    }
    void (async () => {
      try {
        const res = await api.get(`/rh/employes/${raw}`);
        setData(res.data as Record<string, unknown>);
      } catch {
        toast.error("Dossier introuvable ou accès refusé.");
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitLier = async (e: React.FormEvent) => {
    e.preventDefault();
    const rawId = Number(id);
    const aid = Number(lierAgentId);
    if (!Number.isInteger(aid) || aid <= 0) {
      toast.error("Identifiant agent invalide.");
      return;
    }
    setLierLoading(true);
    try {
      await api.put(`/rh/employes/${rawId}`, { agentTerrainId: aid });
      toast.success("Agent terrain lié au dossier RH.");
      setParams({});
      setLierAgentId("");
      load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      toast.error(ax.response?.data?.error ?? "Liaison impossible.");
    } finally {
      setLierLoading(false);
    }
  };

  const agentTerrain = data?.agentTerrain as
    | Record<string, unknown>
    | null
    | undefined;

  const nomComplet = String(data?.nomComplet ?? "");
  const matricule = String(data?.matriculeRH ?? "");

  const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
    { id: "dossier", label: "Dossier", icon: <User className="h-4 w-4" /> },
    { id: "contrat", label: "Contrat", icon: <FileText className="h-4 w-4" /> },
    { id: "paie", label: "Paie", icon: <Wallet className="h-4 w-4" /> },
    { id: "documents", label: "Documents", icon: <FolderOpen className="h-4 w-4" /> },
    { id: "historique", label: "Historique", icon: <History className="h-4 w-4" /> },
  ];

  const historiques = (agentTerrain?.historiquesRH as Record<string, unknown>[] | undefined) ?? [];
  const documentsListe =
    (agentTerrain?.documentsRH as Record<string, unknown>[] | undefined) ?? [];

  return (
    <div className="space-y-6 text-slate-100">
      <AdminPageHero
        eyebrow="Dossier RH"
        title={nomComplet || matricule || "Employé RH"}
        subtitle={`${matricule} · ${String(data?.statutRH ?? "")}`}
        actions={
          <>
            <Link
              to="/admin/rh/employes"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2.5 text-sm text-white hover:bg-slate-800"
            >
              <ArrowLeft className="h-4 w-4" /> Dashboard RH
            </Link>
            {agentTerrain?.id != null ? (
              <Link
                to={`/admin/agents/${String(agentTerrain.id)}`}
                className="inline-flex items-center gap-2 rounded-xl border border-teal-600/60 bg-teal-950/50 px-4 py-2.5 text-sm text-teal-100 hover:bg-teal-950/70"
              >
                Voir fiche terrain
              </Link>
            ) : canManage ? (
              <Link
                to={`/admin/rh/employes/${id}?lier=1`}
                className="inline-flex items-center gap-2 rounded-xl border border-violet-500/50 bg-violet-950/40 px-4 py-2.5 text-sm text-violet-100 hover:bg-violet-950/70"
              >
                Lier à un agent terrain
              </Link>
            ) : null}
          </>
        }
      />

      {showLierForm && !agentTerrain?.id ? (
        <form
          onSubmit={(e) => void submitLier(e)}
          className="rounded-2xl border border-violet-500/40 bg-violet-950/20 p-4"
        >
          <p className="text-sm font-medium text-violet-100">Lier ce dossier à un agent terrain</p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="text-slate-400">ID agent terrain</span>
              <input
                className="mt-1 block w-48 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-white"
                value={lierAgentId}
                onChange={(e) => setLierAgentId(e.target.value)}
                placeholder="ex. 42"
              />
            </label>
            <button
              type="submit"
              disabled={lierLoading}
              className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-500 disabled:opacity-50"
            >
              {lierLoading ? "…" : "Lier"}
            </button>
            <button
              type="button"
              className="text-sm text-slate-400 hover:text-white"
              onClick={() => setParams({})}
            >
              Annuler
            </button>
          </div>
        </form>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400">
          <Loader2 className="h-10 w-10 animate-spin" />
        </div>
      ) : !data ? null : (
        <>
          <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
                  tab === t.id
                    ? "bg-violet-600 text-white shadow-md"
                    : "border border-slate-700 bg-slate-900/70 text-slate-300 hover:bg-slate-800"
                }`}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>

          {tab === "dossier" ? (
            <section className="grid gap-4 rounded-2xl border border-slate-800 bg-slate-900/50 p-6 md:grid-cols-2">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Identité (dossier)
                </h3>
                <dl className="mt-2 space-y-2 text-sm">
                  <div>
                    <dt className="text-slate-500">Nom complet</dt>
                    <dd className="text-white">{nomComplet}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Matricule RH</dt>
                    <dd>{matricule}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Téléphone dossier</dt>
                    <dd>{String(data.telephone ?? "—")}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Email</dt>
                    <dd>{String(data.email ?? "—")}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Lien exploitation
                </h3>
                {agentTerrain?.id ? (
                  <p className="mt-2 text-sm text-emerald-100">
                    Lié à l’agent terrain #{String(agentTerrain.id)} —{" "}
                    {String(agentTerrain.prenom)} {String(agentTerrain.nom)}
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">Aucun agent terrain lié.</p>
                )}
                <p className="mt-4 text-xs text-slate-500">
                  Complétude indicative : {String(data.dossierCompletPercent ?? "—")}%
                </p>
              </div>
            </section>
          ) : null}

          {tab === "contrat" ? (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-sm">
              <dl className="grid gap-3 md:grid-cols-2">
                <div>
                  <dt className="text-slate-500">Statut RH</dt>
                  <dd className="text-white">{String(data.statutRH ?? "—")}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Type contrat</dt>
                  <dd>{String(data.typeContrat ?? "—")}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Poste</dt>
                  <dd>{String(data.posteRH ?? "—")}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Dates</dt>
                  <dd>
                    {data.dateEmbauche
                      ? new Date(String(data.dateEmbauche)).toLocaleDateString("fr-FR")
                      : "—"}{" "}
                    →{" "}
                    {data.dateFinContrat
                      ? new Date(String(data.dateFinContrat)).toLocaleDateString("fr-FR")
                      : "—"}
                  </dd>
                </div>
              </dl>
            </section>
          ) : null}

          {tab === "paie" ? (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-sm">
              <dl className="grid gap-3 md:grid-cols-2">
                <div>
                  <dt className="text-slate-500">Salaire mensuel</dt>
                  <dd className="tabular-nums text-white">
                    {data.salaireMensuel != null ? String(data.salaireMensuel) : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-500">Mode</dt>
                  <dd>{String(data.modePaiement ?? "—")}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-slate-500">Compte / RIB</dt>
                  <dd>{String(data.comptePaiement ?? "—")}</dd>
                </div>
              </dl>
            </section>
          ) : null}

          {tab === "documents" ? (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-sm">
              <p className="text-slate-400">
                Manquants (via agent lié) :{" "}
                {(data.documentsManquants as string[] | undefined)?.length
                  ? (data.documentsManquants as string[]).join(", ")
                  : "Aucun signal — ou pas d’agent lié."}
              </p>
              {documentsListe.length > 0 ? (
                <ul className="mt-4 space-y-2">
                  {documentsListe.map((d) => (
                    <li
                      key={String(d.id)}
                      className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2"
                    >
                      {String(d.titre)}{" "}
                      <span className="text-xs text-slate-500">{String(d.categorie ?? "")}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-slate-500">Aucune entrée DocumentRH liée à l’agent.</p>
              )}
            </section>
          ) : null}

          {tab === "historique" ? (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 text-sm">
              {historiques.length === 0 ? (
                <p className="text-slate-500">Aucun historique RH sur l’agent lié.</p>
              ) : (
                <ul className="space-y-2">
                  {historiques.map((h) => (
                    <li
                      key={String(h.id)}
                      className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2"
                    >
                      <span className="font-medium text-violet-200">{String(h.type)}</span> —{" "}
                      {String(h.description)}
                      <span className="mt-1 block text-xs text-slate-500">
                        {h.dateAction
                          ? new Date(String(h.dateAction)).toLocaleString("fr-FR")
                          : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
